import React, { useState, useEffect } from 'react';
import './Pages.css';

const Claims = ({ userRole }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  
  // Create claim states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerPolicies, setCustomerPolicies] = useState([]);
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [newClaim, setNewClaim] = useState({
    client: '',
    policyNumber: '',
    product: '',
    dateOfIncident: '',
    claimAmount: '',
    claimNarration: '',
    claimType: ''
  });

  useEffect(() => {
    fetchClaims();
    fetchCustomers();
  }, []);

  const fetchClaims = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/claims');
      const data = await response.json();

      if (data.success) {
        setClaims(data.claims);
      } else {
        setError('Failed to load claims');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleViewClaim = async (claimId) => {
    setModalLoading(true);
    setShowModal(true);
    
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/claims/${claimId}`);
      const data = await response.json();

      if (data.success) {
        setSelectedClaim(data.claim);
        setNewStatus(data.claim.Status || '');
      } else {
        alert('Failed to load claim details');
      }
    } catch (err) {
      console.error('Error fetching claim details:', err);
      alert('Unable to load claim details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    setUpdatingStatus(true);

    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/claims/${selectedClaim.Id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (data.success) {
        alert('Claim status updated successfully');
        setShowModal(false);
        fetchClaims(); // Refresh the list
      } else {
        alert(data.message || 'Failed to update claim status');
      }
    } catch (err) {
      console.error('Error updating claim status:', err);
      alert('Unable to update claim status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClaim(null);
    setNewStatus('');
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/customers');
      const data = await response.json();
      if (data.success) {
        setCustomers(data.customers);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const handleClientChange = async (clientEmail) => {
    setNewClaim({ ...newClaim, client: clientEmail, policyNumber: '', product: '' });
    setCustomerPolicies([]);
    
    if (!clientEmail) return;
    
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/customers/${clientEmail}/policies`);
      const data = await response.json();
      if (data.success) {
        setCustomerPolicies(data.policies);
      }
    } catch (err) {
      console.error('Error fetching customer policies:', err);
      alert('Unable to load customer policies');
    }
  };

  const handlePolicyChange = (policyNumber) => {
    const selectedPolicy = customerPolicies.find(p => p.PolicyNumber === policyNumber);
    if (selectedPolicy) {
      setNewClaim({
        ...newClaim,
        policyNumber: policyNumber,
        product: selectedPolicy.ProductName
      });
    }
  };

  const handleCreateClaim = async (e) => {
    e.preventDefault();
    
    if (!newClaim.client || !newClaim.policyNumber || !newClaim.dateOfIncident || !newClaim.claimAmount || !newClaim.claimType) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSubmittingClaim(true);
    
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/claims', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newClaim)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Claim created successfully! Claim Number: ${data.claimNumber}`);
        setShowCreateModal(false);
        // Reset form
        setNewClaim({
          client: '',
          policyNumber: '',
          product: '',
          dateOfIncident: '',
          claimAmount: '',
          claimNarration: '',
          claimType: ''
        });
        setCustomerPolicies([]);
        fetchClaims(); // Refresh the list
      } else {
        alert(data.message || 'Failed to create claim');
      }
    } catch (err) {
      console.error('Error creating claim:', err);
      alert('Unable to create claim');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStatusClass = (status) => {
    if (!status) return 'pending';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('approved') || statusLower.includes('settled')) return 'active';
    if (statusLower.includes('rejected') || statusLower.includes('denied')) return 'inactive';
    if (statusLower.includes('pending')) return 'pending';
    return 'under-review';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Claims</h1>
        </div>
        <div className="loading">Loading claims...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Claims Management</h1>
          <p className="page-description">View and manage insurance claims</p>
        </div>
        <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-circle"></i> New Claim
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Claim Number</th>
              <th>Client Name</th>
              <th>Policy Number</th>
              <th>Product</th>
              <th>Claim Type</th>
              <th>Date Reported</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {claims.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">No claims found</td>
              </tr>
            ) : (
              claims.map((claim, index) => (
                <tr key={claim.Id}>
                  <td>{index + 1}</td>
                  <td className="claim-number">{claim.ClaimNumber || 'N/A'}</td>
                  <td className="client-name">{claim.ClientName || 'N/A'}</td>
                  <td>{claim.PolicyNumber || 'N/A'}</td>
                  <td>{claim.ProductName || 'N/A'}</td>
                  <td>{claim.ClaimType || 'N/A'}</td>
                  <td>{claim.DateReported}</td>
                  <td className="amount">{formatCurrency(claim.ClaimAmount)}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(claim.Status)}`}>
                      {claim.Status || 'Pending'}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button 
                      className="btn-view"
                      onClick={() => handleViewClaim(claim.Id)}
                      title="View Claim"
                    >
                      <i className="bi bi-eye"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Claim Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Claim Details</h2>
              <button className="close-button" onClick={closeModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              {modalLoading ? (
                <div className="loading">Loading claim details...</div>
              ) : selectedClaim ? (
                <>
                  {/* Claim Information */}
                  <div className="detail-section">
                    <h3>Claim Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Claim Number:</label>
                        <span>{selectedClaim.ClaimNumber || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Claim Type:</label>
                        <span>{selectedClaim.ClaimType || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date of Incident:</label>
                        <span>{formatDate(selectedClaim.DateOfIncident)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date Reported:</label>
                        <span>{formatDate(selectedClaim.DateReported)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Claim Amount:</label>
                        <span className="amount-highlight">{formatCurrency(selectedClaim.ClaimAmount)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Status:</label>
                        <span className={`status-badge ${getStatusClass(selectedClaim.Status)}`}>
                          {selectedClaim.Status || 'Pending'}
                        </span>
                      </div>
                      <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                        <label>Claim Narration:</label>
                        <span>{selectedClaim.ClaimNarration || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Client Information */}
                  <div className="detail-section" style={{ marginTop: '20px' }}>
                    <h3>Client Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Client Name:</label>
                        <span>{selectedClaim.ClientName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Email:</label>
                        <span>{selectedClaim.ClientEmail || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Phone Number:</label>
                        <span>{selectedClaim.ClientPhone || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>ID Number:</label>
                        <span>{selectedClaim.ClientIDNumber || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Policy Information */}
                  <div className="detail-section" style={{ marginTop: '20px' }}>
                    <h3>Policy Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Policy Number:</label>
                        <span>{selectedClaim.PolicyNumber || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Product:</label>
                        <span>{selectedClaim.ProductName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Registration:</label>
                        <span>{selectedClaim.Registration || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Sum Insured:</label>
                        <span>{formatCurrency(selectedClaim.SumInsured)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Premium:</label>
                        <span>{formatCurrency(selectedClaim.GrossPremium)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Policy Period:</label>
                        <span>{selectedClaim.CommencementDate} - {selectedClaim.ExpiryDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Information */}
                  {selectedClaim.Make && (
                    <div className="detail-section" style={{ marginTop: '20px' }}>
                      <h3>Vehicle Information</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Make:</label>
                          <span>{selectedClaim.Make || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Model:</label>
                          <span>{selectedClaim.Model || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Year:</label>
                          <span>{selectedClaim.YOM || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Chassis Number:</label>
                          <span>{selectedClaim.Chassis || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Engine Number:</label>
                          <span>{selectedClaim.Engine || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents Section */}
                  <div className="detail-section" style={{ marginTop: '20px' }}>
                    <h3>Uploaded Documents</h3>
                    {selectedClaim.ClaimDocuments ? (
                      <div className="documents-container">
                        {(() => {
                          try {
                            const docs = JSON.parse(selectedClaim.ClaimDocuments);
                            return Array.isArray(docs) && docs.length > 0 ? (
                              <div className="documents-grid">
                                {docs.map((doc, index) => (
                                  <div key={index} className="document-card">
                                    <div className="document-icon">
                                      <i className={`bi bi-${doc.type === 'pdf' ? 'file-pdf' : doc.type === 'image' ? 'file-image' : 'file-earmark'}`}></i>
                                    </div>
                                    <div className="document-info">
                                      <p className="document-name">{doc.name || `Document ${index + 1}`}</p>
                                      <p className="document-size">{doc.size || 'N/A'}</p>
                                    </div>
                                    <a 
                                      href={doc.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn-view-doc"
                                      title="View Document"
                                    >
                                      <i className="bi bi-eye"></i> View
                                    </a>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="no-data">No documents uploaded</p>
                            );
                          } catch (e) {
                            return <p className="no-data">No documents uploaded</p>;
                          }
                        })()}
                      </div>
                    ) : (
                      <p className="no-data">No documents uploaded</p>
                    )}
                  </div>

                  {/* Update Status Section - Only for Role 1 */}
                  {userRole === 1 && (
                    <div className="detail-section" style={{ marginTop: '20px' }}>
                      <h3>Update Claim Status</h3>
                      <div className="status-update-container">
                        <div className="form-group">
                          <label>Select New Status:</label>
                          <select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                            className="status-select"
                          >
                            <option value="">-- Select Status --</option>
                            <option value="Pending">Pending</option>
                            <option value="Under Review">Under Review</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Settled">Settled</option>
                          </select>
                        </div>
                        <button 
                          className="primary-btn"
                          onClick={handleUpdateStatus}
                          disabled={updatingStatus || !newStatus || newStatus === selectedClaim.Status}
                        >
                          {updatingStatus ? 'Updating...' : 'Update Status'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="error-message">Failed to load claim details</div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Claim Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Claim</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateClaim}>
              <div className="modal-body">
                <div className="detail-section">
                  <h3>Claim Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Select Client <span className="required">*</span></label>
                      <select
                        value={newClaim.client}
                        onChange={(e) => handleClientChange(e.target.value)}
                        required
                      >
                        <option value="">-- Select Client --</option>
                        {customers.map((customer) => (
                          <option key={customer.Email} value={customer.Email}>
                            {customer.FullName} ({customer.Email})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Select Policy <span className="required">*</span></label>
                      <select
                        value={newClaim.policyNumber}
                        onChange={(e) => handlePolicyChange(e.target.value)}
                        required
                        disabled={!newClaim.client || customerPolicies.length === 0}
                      >
                        <option value="">-- Select Policy --</option>
                        {customerPolicies.map((policy) => (
                          <option key={policy.PolicyNumber} value={policy.PolicyNumber}>
                            {policy.PolicyNumber} - {policy.Registration} ({policy.ProductName})
                          </option>
                        ))}
                      </select>
                      {newClaim.client && customerPolicies.length === 0 && (
                        <small className="form-hint" style={{ color: '#e74c3c' }}>
                          No active policies found for this client
                        </small>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Product</label>
                      <input
                        type="text"
                        value={newClaim.product}
                        readOnly
                        placeholder="Auto-filled from policy"
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>Claim Type <span className="required">*</span></label>
                      <select
                        value={newClaim.claimType}
                        onChange={(e) => setNewClaim({ ...newClaim, claimType: e.target.value })}
                        required
                      >
                        <option value="">-- Select Type --</option>
                        <option value="Accident">Accident</option>
                        <option value="Theft">Theft</option>
                        <option value="Fire">Fire</option>
                        <option value="Natural Disaster">Natural Disaster</option>
                        <option value="Vandalism">Vandalism</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Date of Incident <span className="required">*</span></label>
                      <input
                        type="date"
                        value={newClaim.dateOfIncident}
                        onChange={(e) => setNewClaim({ ...newClaim, dateOfIncident: e.target.value })}
                        max={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Claim Amount (KES) <span className="required">*</span></label>
                      <input
                        type="number"
                        value={newClaim.claimAmount}
                        onChange={(e) => setNewClaim({ ...newClaim, claimAmount: e.target.value })}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Claim Narration <span className="required">*</span></label>
                      <textarea
                        value={newClaim.claimNarration}
                        onChange={(e) => setNewClaim({ ...newClaim, claimNarration: e.target.value })}
                        placeholder="Describe the incident and claim details..."
                        rows="4"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="info-box" style={{ marginTop: '15px', padding: '12px', background: '#e3f2fd', borderRadius: '8px', fontSize: '13px', color: '#1976d2' }}>
                    <i className="bi bi-info-circle"></i> <strong>Note:</strong> Date reported will be set to today's date. Claim number will be auto-generated. Status will be set to "Pending".
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="btn-secondary"
                  disabled={submittingClaim}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-btn"
                  disabled={submittingClaim}
                >
                  {submittingClaim ? 'Creating...' : 'Create Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Claims;
